import API from "utils/API";
import { useState } from "react";
import Address from "typedef/Address";
import { useForm } from "react-hook-form";
import { useLocation } from "react-router-dom";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import useParseToSubmit from "./useParseToSubmit";
import { passwordMsgHelper, regexPassword } from "CONST";
import parseMoney from "utils/parseMoney";

export type RegisterFormDialogs =
  | "terms"
  | "privacy"
  | "verify"
  | "error"
  | "communications"
  | "MAX_OFFER_ERROR";

type Ocuppancy = "Primary Residence" | "Second Home" | "Investment Property";

export type RegisterFormInputs = {
  password: string;
  hasAcceptTerms: boolean;
  hasAcceptCertify: boolean;
  annualIncome: string;
  creditScore: string;
  email: string;
  firstName: string;
  lastName: string;
  phoneNumber: string;
  databaseIdPersona: string;
  reportIdPersona: string;
  inviteCode?: string;
  sourceId?: "fortuna";
  occupancy: Ocuppancy;
  borrowerAddress?: Address;
  address: Address;
  currentLoanBalance: string;
  requestedNewLoan: string;
  useProceeds: string;
  employmentType: string;
  homeValue: string;
  agreeNotifications: boolean;
};

const useRegisterForm = () => {
  const location = useLocation();
  const [errorMessage, setErrorMessage] = useState<string>();
  const [openedDialog, setOpenedDialog] = useState<RegisterFormDialogs>();
  const [loading, setLoading] = useState<boolean>(false);
  const parseToSubmit = useParseToSubmit();

  const schema = yup.object().shape({
    address: yup.mixed().required("Sub property address is required."),
    borrowerAddress: yup.mixed().when(
      ["occupancy", "address"],
      //@ts-ignore
      (occupancy: Ocuppancy, address: Address, schema) => {
        if (occupancy !== "Primary Residence")
          return schema
            .required("Confirm Password is required")
            .test(
              "sakeAddress",
              "Sub property address and Primary home address cannot be the same.",
              (borrowerValue: Address) => {
                return (
                  JSON.stringify(address) !== JSON.stringify(borrowerValue)
                );
              }
            );
      }
    ),
    creditScore: yup.string().required("Credit score is required."),
    useProceeds: yup.string().required("Use of proceeds is required."),
    occupancy: yup.string().required("Occupancy type is required."),
    employmentType: yup.string().required("Employment type is required."),
    email: yup
      .string()
      .email("Email has invalid format.")
      .required("Email is required."),
    phoneNumber: yup
      .string()
      .required("Phone number is required.")
      .test(
        "minLenght",
        "The phone number must be 11 characters long.",
        (value) => {
          if (!value) return true;
          if (process.env.REACT_APP_ENV === "prod") {
            return value.replaceAll(/ /g, "").length === 12;
          }
          return true;
        }
      ),
    password: yup
      .string()
      .required("Password is required.")
      .max(100, "The password must be less than 100 characters.")
      .min(9, "The password must be at least 9 characters long.")
      .matches(regexPassword, passwordMsgHelper),
    firstName: yup.string().required("Legal first name is required."),
    lastName: yup.string().required("Legal Last name is required."),
    homeValue: yup
      .string()
      .required("Home value is required.")
      .test("min", "Home value cannot be less than $100,000", (value) => {
        if (!value) return true;
        if (parseMoney(value) < 100_000) return false;
        return true;
      }),
    requestedNewLoan: yup
      .string()
      .required("Request loan amount is required.")
      .test(
        "betweenAmount",
        "Loan Amount should be between $25,000 - $250,000",
        (value) => {
          if (!value) return true;
          const amount = parseMoney(value);
          if (amount < 25_000 || amount > 250_000) return false;
          return true;
        }
      ),
    hasAcceptTerms: yup.boolean().required("Accept terms and conditions."),
    hasAcceptCertify: yup.boolean().required("Accept certify."),
    annualIncome: yup
      .string()
      .min(8, "You must enter a number of at least 5 digits.")
      .required("Total household income is required."),
    agreeNotifications: yup.boolean().required("Agree notifications."),
    currentLoanBalance: yup
      .string()
      .required("Current loan balance is required"),
  });

  const form = useForm<RegisterFormInputs>({
    mode: "onChange",
    defaultValues: {
      address: location.state as Address,
      borrowerAddress: undefined,
      creditScore: "",
      useProceeds: undefined,
      occupancy: "Primary Residence",
      employmentType: "",
      email: "",
      password: "",
      firstName: "",
      phoneNumber: process.env.REACT_APP_ENV === "prod" ? "+1" : "",
      lastName: "",
      homeValue: "",
      requestedNewLoan: "",
      hasAcceptTerms: false,
      hasAcceptCertify: false,
      annualIncome: "",
      agreeNotifications: false,
      currentLoanBalance: "",
    },
    resolver: yupResolver(schema),
  });

  const onSaveUnqualified = (error?: { id: string; message: string }) =>
    form.handleSubmit(async (data) => {
      setLoading(true);
      try {
        const response = await API.post({
          url: `/save-unqualified`,
          data: {
            ...parseToSubmit(data),
            error,
            password: undefined,
          },
        });
        if ("error" in response) {
          setErrorMessage(response.error);
          setOpenedDialog("error");
        } else if (openedDialog === "MAX_OFFER_ERROR") {
          setErrorMessage(
            "Unfortunately based on our underwriting guidelines we are unable to offer you a loan.\n\nPlease feel free to reach out to support:\n\nsupport@nftydoor.com"
          );
          setOpenedDialog("error");
        }
      } catch (unknownError) {
        setErrorMessage(String(unknownError));
        console.error(unknownError);
      }
      setLoading(false);
    })();

  const onSubmit = form.handleSubmit(async (data) => {
    setLoading(true);
    try {
      const response = await API.post({
        url: `/register-borrower`,
        data: parseToSubmit(data),
      });
      if ("error" in response) {
        setOpenedDialog(
          response.errorId === "MAX_OFFER_ERROR" ? "MAX_OFFER_ERROR" : "error"
        );
        setErrorMessage(response.error);

        response.errorId !== "MAX_OFFER_ERROR" &&
          onSaveUnqualified(
            response.errorId
              ? {
                  id: response.errorId ?? "",
                  message: response.error ?? "",
                }
              : undefined
          );
      } else {
        setOpenedDialog("verify");
      }
    } catch (unknownError) {
      setErrorMessage(String(unknownError));
      console.error(unknownError);
    }
    setLoading(false);
  });

  return {
    onSaveUnqualified,
    onSubmit,
    openedDialog,
    errorMessage,
    setOpenedDialog,
    setErrorMessage,
    loading,
    ...form,
  };
};

export default useRegisterForm;
